import { ApiResponse } from './api';

export enum ReconciliationPhase {
  EXECUTION = 'EXECUTION',
  IMPORT_EXECUTION_FILE = 'IMPORT_EXECUTION_FILE',
}

/** Reconciliation run row (list endpoint); supports snake_case or camelCase for dates/type. */
export type ReconciliationRun = {
  id: number;
  run_date?: string;
  createdAt?: string;
  run_type?: string;
  runType?: string;
  status: string;
  phase?: string;
};

export type ReconciliationRunResponse = ApiResponse<{
  items: ReconciliationRun[];
  totalItems: number;
}>;

/** Single run payload from GET /admin/reconciliation/runs/:runId */
export type ReconciliationRunDetail = {
  id: number;
  orderId: string;
  runType: string;
  phase: string;
  provider: string;
  status: string;
  executionFileName: string | null;
  totalItems: number;
  passedItems: number;
  failedItems: number;
  triggeredBy: number;
  createdAt: string;
  updatedAt: string;
  /** When backend adds execution file upload time */
  executionFileUploadedAt?: string | null;
};

export type ReconciliationRunItem = {
  id: number;
  reconciliationRunId: number;
  userId: string;
  fundId: string;
  fundName: string;
  orderId: string;
  itemStatus: string;
  createdAt: string;
  updatedAt: string;
};

export type ReconciliationRunDetailData = {
  run: ReconciliationRunDetail;
  items: ReconciliationRunItem[];
  totalItems: number;
};

export type ReconciliationRunDetailResponse = ApiResponse<ReconciliationRunDetailData>;

/** Single comparison row from GET .../runs/:runId/items/:itemId (or embedded in export). */
export type ReconciliationItemComparison = {
  id: number;
  reconciliationItemId: number;
  step: number;
  metricName: string;
  isMatch: boolean;
  message?: string | null;
  systemValue?: string | null;
  fileValue?: string | null;
  delta?: string | null;
  details?: unknown | null;
  createdAt?: string;
  updatedAt?: string;
};

/** Item with full comparisons from GET .../runs/:runId/items/:itemId */
export type ReconciliationRunItemDetail = ReconciliationRunItem & {
  comparisons: ReconciliationItemComparison[];
};

export type ReconciliationRunItemDetailResponse = ApiResponse<ReconciliationRunItemDetail>;

/** One comparison on an item (from GET .../export). Supports camelCase or snake_case. */
export type ReconciliationItemMetricComparison = {
  step?: string | number | null;
  metricName?: string;
  metric_name?: string;
  /** Preferred pass/fail flag from API */
  isMatch?: boolean | number | null;
  systemValue?: string | number | boolean | null;
  system_value?: string | number | boolean | null;
  fileValue?: string | number | boolean | null;
  file_value?: string | number | boolean | null;
  delta?: string | number | null;
  message?: string | null;
  /** Legacy match flag if `isMatch` is absent */
  match?: boolean | string | number | null;
};

/** Item row from export API including per-metric comparisons. */
export type ReconciliationRunExportItem = ReconciliationRunItem & {
  metrics?: ReconciliationItemMetricComparison[];
  comparisons?: ReconciliationItemMetricComparison[];
  metricComparisons?: ReconciliationItemMetricComparison[];
};

/** Full export JSON: run + all items and their metric-level comparisons. */
export type ReconciliationRunExportData = {
  run: ReconciliationRunDetail;
  items: ReconciliationRunExportItem[];
  totalItems?: number;
};

export type ReconciliationRunExportResponse = ApiResponse<ReconciliationRunExportData>;
